/**
 * AdminRenewalPreferenceModal — admin sets or resets the package (product ×
 * billing interval) a subscription renews on for its next period (US-2, AC-12).
 *
 * Owner decision (2026-10-08): no prices — the admin picks an ACTIVE PACKAGE from
 * the brand's admin catalog (CreateOrderModal pattern). Selectability (AC-4) and
 * blocks (AC-5) are enforced by the backend: 400 / 409 are shown inline.
 *
 * @see docs/specifications/2026-09-28-wybor-pakietu-na-kolejny-okres/spec.md AC-12
 * @see docs/specifications/2026-09-28-wybor-pakietu-na-kolejny-okres/reviews/consolidated.md H-11
 */
import React, { useEffect, useId, useRef, useState } from "react";
import { useProductsQuery } from "../../../../hooks/useAdminProductCatalog";
import { useFocusTrap } from "../../../../hooks/useFocusTrap";
import {
  setAdminRenewalPreference,
  clearAdminRenewalPreference,
} from "../../../../services/adminProductCatalogApi";
import { mapRenewalPreferenceErrorCode } from "../../../../components/renewal-preference-error-code-mapper";
import { BILLING_INTERVAL_LABELS } from "./subscription-display.constants";
import type {
  AdminRenewalPreferenceResult,
  BillingProduct,
  BillingSubscription,
  RenewalBillingInterval,
} from "../../../../types/adminBilling";

/** Maximum products fetched for the package picker (same as CreateOrderModal). */
const MAX_PRODUCTS_FOR_PICKER = 100;

const RENEWAL_INTERVALS: RenewalBillingInterval[] = ["monthly", "quarterly", "yearly"];

const FEATURE_DISABLED_MESSAGE = "Funkcja zmiany pakietu jest wyłączona.";
const NOT_SELECTABLE_MESSAGE =
  "Wybrany pakiet nie jest dostępny jako cel odnowienia dla tej subskrypcji.";
const GENERIC_ERROR_MESSAGE = "Nie udało się zapisać zmiany. Spróbuj ponownie.";
const PROFORMA_PENDING_SUFFIX = " Proforma dotrze do klienta mailem w ciągu kilku minut.";

type RenewalAction = "set" | "clear";

type HttpErrorLike = {
  response?: { status?: number; data?: { errorCode?: string } };
};

const isRenewalInterval = (value: unknown): value is RenewalBillingInterval =>
  RENEWAL_INTERVALS.includes(value as RenewalBillingInterval);

const intervalLabel = (interval: string): string =>
  BILLING_INTERVAL_LABELS[interval] ?? interval;

const capitalize = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

const errorStatus = (err: unknown): number | undefined =>
  (err as HttpErrorLike)?.response?.status;

/** Maps a failed PUT/DELETE to an inline Polish message (AC-12 error contract). */
function describeRenewalPreferenceError(err: unknown): string {
  switch (errorStatus(err)) {
    case 409: {
      const errorCode = (err as HttpErrorLike).response?.data?.errorCode;
      const message = mapRenewalPreferenceErrorCode(errorCode);
      // The shared mapper speaks to the client; the raw code helps support.
      return errorCode ? `${message} (kod: ${errorCode})` : message;
    }
    case 400:
      return NOT_SELECTABLE_MESSAGE;
    case 404:
      return FEATURE_DISABLED_MESSAGE;
    default:
      return GENERIC_ERROR_MESSAGE;
  }
}

export interface AdminRenewalPreferenceModalProps {
  subscription: BillingSubscription;
  brandId: string;
  onClose: () => void;
  /** Called after a successful PUT/DELETE — the parent should refresh the subscription list. */
  onSaved: () => void;
}

export const AdminRenewalPreferenceModal: React.FC<AdminRenewalPreferenceModalProps> = ({
  subscription,
  brandId,
  onClose,
  onSaved,
}) => {
  const titleId = useId();
  const contextId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const productSelectRef = useRef<HTMLSelectElement>(null);
  const closeAfterSuccessRef = useRef<HTMLButtonElement>(null);

  const {
    data: productsData,
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
  } = useProductsQuery(brandId, { limit: MAX_PRODUCTS_FOR_PICKER });
  const activePackages: BillingProduct[] = (productsData?.data ?? []).filter(
    (p) => p.productType === "PACKAGE" && p.status === "ACTIVE"
  );
  const noProducts = !isLoadingProducts && activePackages.length === 0;

  const currentProductId = subscription.product?.id ?? subscription.productId ?? "";
  const [productId, setProductId] = useState(currentProductId);
  const [billingInterval, setBillingInterval] = useState<RenewalBillingInterval>(
    isRenewalInterval(subscription.billingInterval) ? subscription.billingInterval : "monthly"
  );
  // Only a package actually offered in the picker can be submitted (e.g. the
  // current product may be archived) — otherwise the admin must choose.
  const selectedProductId = activePackages.some((p) => p.id === productId) ? productId : "";

  const [pendingAction, setPendingAction] = useState<RenewalAction | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const isSaving = pendingAction !== null;
  const actionsDisabled = isSaving || featureDisabled;

  // Declared before the auto-focus effect so the trap remembers the trigger
  // button (not the select) and returns focus to it on close.
  useFocusTrap(dialogRef, true);

  useEffect(() => {
    const select = productSelectRef.current;
    (select && !select.disabled ? select : dialogRef.current)?.focus();
  }, []);

  useEffect(() => {
    if (successMessage) closeAfterSuccessRef.current?.focus();
  }, [successMessage]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSaving) onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose, isSaving]);

  const productName = (id: string): string =>
    activePackages.find((p) => p.id === id)?.name ??
    (subscription.product?.id === id ? subscription.product.name : id);

  const buildSuccessMessage = (
    action: RenewalAction,
    result: AdminRenewalPreferenceResult
  ): string => {
    const target = `${productName(result.productId)}, interwał ${intervalLabel(result.billingInterval)}`;
    const base =
      action === "set"
        ? `Zapisano. Kolejny okres: ${target}.`
        : `Przywrócono domyślny pakiet na kolejny okres: ${target}.`;
    return result.proformaPending ? `${base}${PROFORMA_PENDING_SUFFIX}` : base;
  };

  const runAction = async (action: RenewalAction) => {
    if (actionsDisabled) return;
    if (action === "set" && !selectedProductId) return;
    setPendingAction(action);
    setErrorMessage(null);
    try {
      const result =
        action === "set"
          ? await setAdminRenewalPreference(
              subscription.id,
              { productId: selectedProductId, billingInterval },
              brandId
            )
          : await clearAdminRenewalPreference(subscription.id, brandId);
      onSaved();
      setSuccessMessage(buildSuccessMessage(action, result));
    } catch (err) {
      if (errorStatus(err) === 404) setFeatureDisabled(true);
      setErrorMessage(describeRenewalPreferenceError(err));
    } finally {
      setPendingAction(null);
    }
  };

  const currentSummary = [
    subscription.product?.name ?? "brak produktu",
    subscription.billingInterval ? intervalLabel(subscription.billingInterval) : null,
    subscription.currentPeriodEnd
      ? `okres do ${new Date(subscription.currentPeriodEnd).toLocaleDateString("pl-PL")}`
      : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={contextId}
        tabIndex={-1}
        className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl focus:outline-none"
        data-testid="admin-renewal-preference-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h2 id={titleId} className="text-lg font-semibold text-neutral-900">
            Pakiet na kolejny okres
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600 disabled:opacity-50"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p id={contextId} className="text-xs text-neutral-500">
            {subscription.tenantName ?? subscription.tenantId} — obecnie: {currentSummary}
          </p>

          {successMessage ? (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md" role="status">
              <p className="text-sm text-green-800">{successMessage}</p>
              <button
                ref={closeAfterSuccessRef}
                type="button"
                onClick={onClose}
                className="mt-3 px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Zamknij
              </button>
            </div>
          ) : (
            <>
              <p className="text-xs text-neutral-500">
                Wybór dotyczy tylko najbliższego odnowienia — proformę wystawi system 7 dni
                przed końcem okresu. Dostępność pakietu sprawdzamy przy zapisie.
              </p>

              <div>
                <label
                  htmlFor="renewal-preference-product"
                  className="block text-sm font-medium text-neutral-700 mb-1"
                >
                  Pakiet *
                </label>
                <select
                  ref={productSelectRef}
                  id="renewal-preference-product"
                  value={selectedProductId}
                  onChange={(e) => setProductId(e.target.value)}
                  disabled={isLoadingProducts || noProducts || actionsDisabled}
                  className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-50 disabled:text-neutral-500"
                >
                  <option value="">
                    {isLoadingProducts
                      ? "Ładowanie..."
                      : noProducts
                      ? "Brak dostępnych pakietów"
                      : "Wybierz pakiet..."}
                  </option>
                  {activePackages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {isErrorProducts && (
                  <p className="text-sm text-red-600 mt-1">Błąd ładowania pakietów</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="renewal-preference-interval"
                  className="block text-sm font-medium text-neutral-700 mb-1"
                >
                  Interwał rozliczeniowy *
                </label>
                <select
                  id="renewal-preference-interval"
                  value={billingInterval}
                  onChange={(e) => setBillingInterval(e.target.value as RenewalBillingInterval)}
                  disabled={actionsDisabled}
                  className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-50 disabled:text-neutral-500"
                >
                  {RENEWAL_INTERVALS.map((interval) => (
                    <option key={interval} value={interval}>
                      {capitalize(intervalLabel(interval))}
                    </option>
                  ))}
                </select>
              </div>

              {errorMessage && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md" role="alert">
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!successMessage && (
          <div className="px-6 py-4 border-t border-neutral-200 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => runAction("clear")}
              disabled={actionsDisabled}
              className="text-sm font-medium text-primary-700 underline hover:text-primary-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pendingAction === "clear" ? "Przywracanie..." : "Przywróć domyślny pakiet"}
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="px-4 py-2 text-sm border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={() => runAction("set")}
                disabled={actionsDisabled || !selectedProductId}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {pendingAction === "set" ? "Zapisywanie..." : "Zapisz"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRenewalPreferenceModal;
